/**
 * The offline copy follows the live streams (T07-42, R21, design/05 §4). Before, the copy was
 * only read at sign-in and every 5 minutes — a card that became sellable after the sign-in (a new
 * card put on sale, a hold that ended) stayed 「在庫なし」 in the copy, so it could not be sold
 * when the network dropped a minute later (`@staging-offline-two-sales`). Now, while online:
 *
 * - inventory-service's `inventory.changed:*` (the register's one subscription, already scoped to
 *   this shop) → the card's row in the copy gets the new `available` / `quantity` / `reason` at
 *   once (`availabilityConfirmedAt` = when the register heard it);
 * - a card the copy does not have yet turns available, the hub's `pos:register:<id>` says a
 *   product / its availability changed (catalog upsert, `variant availability_changed`), or the
 *   inventory stream had to resync → a delta read of `GET /pos/snapshot` (the server's watermark,
 *   `since=`), debounced so a burst of changes is one read;
 * - and still at sign-in + every 5 minutes (a slow safety net).
 *
 * The copy stays display-only: prices come from the server, the offline rules (T07-33) decide
 * what may be sold, and the server's hold is the final guard when the sale is uploaded.
 */
import { useRealtime } from "@tcg/realtime/react";
import { useEffect, useRef } from "react";

import { usePos } from "@/app/context";
import type { PosDatabase } from "@/lib/db";
import { SNAPSHOT_INTERVAL_MS, syncSnapshot } from "@/lib/snapshot";

import { ALL_INVENTORY_TOPIC, type LiveAvailability } from "./availability";

/** A burst of changes (a whole box put on sale) is one delta read. */
export const SNAPSHOT_DEBOUNCE_MS = 1_500;

/** Hub entities of `pos:register:<id>` that may add / change cards of the copy. */
const CATALOG_ENTITIES = new Set(["product", "variant"]);

/**
 * Write what the stream said into the copy. Returns false when the copy has no such card (a new
 * one → the caller reads the delta).
 */
export async function applyToSnapshot(
  db: PosDatabase,
  variantId: string,
  live: LiveAvailability,
): Promise<boolean> {
  const updated = await db.snapshotItems.update(variantId, {
    available: live.available,
    quantity: live.quantity,
    reason: live.reason,
    availabilityConfirmedAt: live.receivedAt,
  });
  return updated > 0;
}

/** Runs `run` at most once at a time; a trigger while it runs runs it once more afterwards. */
export class DeltaScheduler {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private again = false;

  constructor(
    private readonly run: () => Promise<unknown>,
    private readonly delayMs = SNAPSHOT_DEBOUNCE_MS,
  ) {}

  trigger(): void {
    if (this.timer) {
      return;
    }
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.now();
    }, this.delayMs);
  }

  async now(): Promise<void> {
    if (this.running) {
      this.again = true;
      return;
    }
    this.running = true;
    try {
      await this.run();
    } catch {
      // the next trigger / the 5-minute run tries again
    } finally {
      this.running = false;
    }
    if (this.again) {
      this.again = false;
      await this.now();
    }
  }

  stop(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.again = false;
  }
}

export interface SnapshotFeedOptions {
  /** Signed in, online and the register's shop known. */
  enabled: boolean;
  locationId: string | null;
  /** `pos:register:<id>` of this tablet. */
  topic: string | null;
}

export function useSnapshotFeed({ enabled, locationId, topic }: SnapshotFeedOptions): void {
  const { api, db, availability } = usePos();
  const scheduler = useRef<DeltaScheduler | null>(null);

  useEffect(() => {
    if (!enabled || !locationId) {
      return;
    }
    const delta = new DeltaScheduler(() => syncSnapshot(api, db, locationId));
    scheduler.current = delta;
    void delta.now(); // at sign-in (and when back online)
    const timer = setInterval(() => void delta.now(), SNAPSHOT_INTERVAL_MS);
    const stopListening = availability.onChange((variantId, live) => {
      void applyToSnapshot(db, variantId, live).then((known) => {
        if (!known && live.available) {
          delta.trigger(); // a card the copy does not have yet
        }
      });
    });
    return () => {
      stopListening();
      clearInterval(timer);
      delta.stop();
      scheduler.current = null;
    };
  }, [api, availability, db, enabled, locationId]);

  // new / changed products (hub) and a resync of the inventory stream → a delta read
  useRealtime(
    topic ? [topic] : [],
    (message) => {
      if (message.entity && CATALOG_ENTITIES.has(message.entity)) {
        scheduler.current?.trigger();
      }
    },
    { enabled: enabled && Boolean(topic) },
  );
  useRealtime([ALL_INVENTORY_TOPIC], undefined, {
    enabled: enabled && Boolean(locationId),
    onResync: () => scheduler.current?.trigger(),
  });
}
